import type { ReactNode } from "react"
import React, { createContext, useState, useContext, useEffect } from "react"
import { DateTime } from "luxon"
import useMySettings from "api/useMySettings"
import { useSearchParams, useLocation } from "react-router-dom"
import { useCustomerData } from "context/customer-provider"
import { useCustomerConfig, UNIFIED_DETECTOR_CONFIDENCE_MIN } from "config/customerConfigurations"

// React Router's setSearchParams(prev => …) hands `prev` from the last render, but
// BrowserRouter commits URL changes inside startTransition, so `prev` can lag the
// address bar. Route View's Forward/Back push frame_id straight away; crossing onto
// a new USRN then syncs assetId here, and building from a stale `prev` put the old
// frame_id back — Forward got stuck on the last frame before every USRN change.
// window.location is updated synchronously by history.pushState, so build from it.
const liveSearchParams = () => new URLSearchParams(window.location.search)

type CalendarValueType = Date

interface VehicleIds {
  options: string[]
  selected: string[]
}
interface AssetCodes {
  options: { usrn: string; coordinates: number[] }[]
  selected: string
}

export interface Filters {
  startDate: string
  endDate: string
  startTime: CalendarValueType
  endTime: CalendarValueType
  vehicleIds: VehicleIds
  minConfidence: number
  minModelatorConfidence: number
  framesFromJourneyOnly: boolean
  assetCodes: AssetCodes
}

// Default initial filters (used for backward compatibility)
// Note: The provider now initializes with customer-specific values
export const initialFilters: Filters = {
  startDate: DateTime.now().minus({ days: 30 }).startOf("day").toISO(),
  endDate: DateTime.now().endOf("day").toISO(),
  startTime: DateTime.now().set({ hour: 0, minute: 0, second: 0 }).toJSDate(),
  endTime: DateTime.now().set({ hour: 23, minute: 59, second: 59 }).toJSDate(),
  vehicleIds: { options: [], selected: [] },
  minConfidence: 60,
  minModelatorConfidence: 62,
  framesFromJourneyOnly: true,
  assetCodes: { options: [], selected: "" },
}

interface FilterContextType {
  filters: Filters
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
  filtersLoading: boolean
  setFiltersLoading: React.Dispatch<React.SetStateAction<boolean>>
}

const FilterContext = createContext<FilterContextType | undefined>(undefined)

interface FilterProviderProps {
  children: ReactNode
}

export const RouteViewFiltersProvider: React.FC<FilterProviderProps> = ({ children }) => {
  const { customerId } = useCustomerData()
  const customerConfig = useCustomerConfig(customerId)
  const mySettings = useMySettings()
  const userAssignedVehicle = mySettings.vehicle || null

  // Initialize filters with customer-specific confidence values
  const defaultConfidence = UNIFIED_DETECTOR_CONFIDENCE_MIN
  const defaultModelatorConfidence = 0 // ART-3048: modelator threshold retired

  const [filters, setFilters] = useState<Filters>(() => ({
    startDate: DateTime.now().minus({ days: 30 }).startOf("day").toISO(),
    endDate: DateTime.now().endOf("day").toISO(),
    startTime: DateTime.now().set({ hour: 0, minute: 0, second: 0 }).toJSDate(),
    endTime: DateTime.now().set({ hour: 23, minute: 59, second: 59 }).toJSDate(),
    vehicleIds: { options: [], selected: [] },
    minConfidence: defaultConfidence,
    minModelatorConfidence: defaultModelatorConfidence,
    framesFromJourneyOnly: true,
    assetCodes: { options: [], selected: "" },
  }))
  const [filtersLoading, setFiltersLoading] = useState<boolean>(true)

  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const isRouteView = location.pathname.startsWith("/route-view")
  const urlAssetId = searchParams.get("assetId")
  const urlVehicle = searchParams.get("vehicle")

  // Handle vehicle URL parameter
  useEffect(() => {
    if (!isRouteView || !urlVehicle) return
    // Set the vehicle filter from URL param
    setFilters((prevFilters) => ({
      ...prevFilters,
      vehicleIds: {
        ...prevFilters.vehicleIds,
        selected: [urlVehicle],
      },
    }))
    // Remove the URL param after applying it (one-time application)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete("vehicle")
      return next
    })
  }, [urlVehicle, isRouteView, setSearchParams, setFilters])

  // Update confidence thresholds when customer changes
  useEffect(() => {
    if (customerId) {
      setFilters((prev) => ({
        ...prev,
        minConfidence: defaultConfidence,
        minModelatorConfidence: defaultModelatorConfidence,
      }))
    }
  }, [customerId, defaultConfidence, defaultModelatorConfidence])

  useEffect(() => {
    if (!isRouteView) return
    if (urlAssetId && filters.assetCodes.options.some((opt) => opt.usrn === urlAssetId)) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        assetCodes: {
          ...prevFilters.assetCodes,
          selected: urlAssetId,
        },
      }))
    } else if (
      urlAssetId &&
      !filters.assetCodes.options.some((opt) => opt.usrn === urlAssetId)
    ) {
      // Handle invalid URL parameter — only remove the assetId param
      setSearchParams(() => {
        const next = liveSearchParams()
        next.delete("assetId")
        return next
      }, { replace: true })
    }
  }, [urlAssetId, filters.assetCodes.options, setSearchParams, isRouteView, setFilters])

  // Update URL whenever the selected asset changes
  useEffect(() => {
    if (!isRouteView) return
    if (filters.assetCodes.selected) {
      setSearchParams(() => {
        const next = liveSearchParams()
        next.set("assetId", filters.assetCodes.selected)
        return next
      }, { replace: true })
    } else {
      // Only remove assetId if the browser URL still has it.
      // During a close-journey transition, navigate() already pushed a clean URL
      // but React Router's internal state is stale — calling setSearchParams would
      // re-push the old /route-view/journeys pathname. Checking window.location
      // (which IS updated synchronously by navigate's history.pushState) avoids this.
      const browserParams = new URLSearchParams(window.location.search)
      if (browserParams.has("assetId")) {
        setSearchParams(() => {
          const next = liveSearchParams()
          next.delete("assetId")
          return next
        }, { replace: true })
      }
    }
  }, [filters.assetCodes.selected, setSearchParams, isRouteView])

  // When navigating away from Route View, clear selected asset to avoid re-adding it
  useEffect(() => {
    if (!isRouteView && filters.assetCodes.selected) {
      setFilters((prev) => ({
        ...prev,
        assetCodes: { ...prev.assetCodes, selected: "" },
      }))
    }
  }, [isRouteView, setFilters, filters.assetCodes.selected])

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      vehicleIds: {
        options: [],
        selected: [],
      },
    }))
  }, [userAssignedVehicle])

  return (
    <FilterContext.Provider value={{ filters, setFilters, filtersLoading, setFiltersLoading }}>
      {children}
    </FilterContext.Provider>
  )
}

export const useFilters = (): FilterContextType => {
  const context = useContext(FilterContext)
  if (!context) {
    throw new Error("useFilters must be used within a FilterProvider")
  }
  return context
}
